'use client'

import { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'

interface HppCheckoutModalProps {
    open: boolean
    checkoutUrl: string | null
    onOpenChange: (open: boolean) => void
    onPaymentComplete: () => void | Promise<void>
}

export function HppCheckoutModal({
    open,
    checkoutUrl,
    onOpenChange,
    onPaymentComplete,
}: HppCheckoutModalProps) {
    const [isActivating, setIsActivating] = useState(false)

    const handlePaymentComplete = async () => {
        setIsActivating(true)
        try {
            await onPaymentComplete()
        } finally {
            setIsActivating(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='flex h-[90vh] w-[95vw] max-w-4xl flex-col gap-0 overflow-hidden p-0'>
                <DialogHeader className='shrink-0 border-b px-4 py-3 pr-12'>
                    <DialogTitle>Complete Your Subscription</DialogTitle>
                    <DialogDescription>
                        Complete payment below, then confirm to activate your Professional plan.
                    </DialogDescription>
                </DialogHeader>

                {checkoutUrl ? (
                    <iframe
                        src={checkoutUrl}
                        title='Secure payment'
                        className='min-h-0 flex-1 w-full border-0 bg-white'
                        allow='payment *'
                    />
                ) : (
                    <div className='flex flex-1 items-center justify-center text-muted-foreground'>
                        Loading payment form...
                    </div>
                )}

                <div className='flex shrink-0 flex-wrap items-center justify-between gap-3 border-t px-4 py-3'>
                    <div className='flex flex-col gap-1'>
                        <p className='text-xs text-muted-foreground'>
                            After payment succeeds, click below — your plan activates automatically.
                        </p>
                        {checkoutUrl ? (
                            <a
                                href={checkoutUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-xs text-primary hover:underline'
                            >
                                Open payment page in a new tab
                            </a>
                        ) : null}
                    </div>
                    <Button
                        size='sm'
                        onClick={handlePaymentComplete}
                        disabled={isActivating}
                    >
                        {isActivating ? (
                            <>
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                Activating...
                            </>
                        ) : (
                            "I've completed payment"
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
